/**
 * Klaverjassen at the table, everyone on their own phone.
 *
 * The third play screen, and structurally the same as the other two: the shell,
 * the table, the fanned hand. What is new is the trump-choosing panel, and that
 * the score under your name belongs to your team rather than to you.
 */

import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { Card, Suit } from '@shared/types/game';
import {
  SUIT_SYMBOLS,
  cardsEqual,
  klaverjasLegalPlays,
  rankLabel,
  sortKlaverjasHand,
  teamOf,
  whyIllegal,
} from '@shared/game-logic';
import { Button, Panel, Screen } from '@/components/ui';
import { PlayingCard } from '@/components/PlayingCard';
import FannedHand from '@/components/FannedHand';
import Avatar from '@/components/Avatar';
import KlaverjasTable from '@/components/KlaverjasTable';
import Notices from '@/components/Notices';
import { useKlaverjasSession, type KlaverjasActions } from '@/live/useKlaverjasSession';
import { playCue } from '@/lib/feedback';
import { SUIT_LABELS, seatColor } from '@/lib/labels';
import {
  isMyChoice,
  isMyKlaverjasTurn,
  myKlaverjasHand,
  myKlaverjasPlayer,
  useKlaverjasLive,
} from '@/store/klaverjasLive';

const SUITS: Suit[] = ['clubs', 'hearts', 'spades', 'diamonds'];
const cardName = (card: Card) => `${rankLabel(card.rank)} ${SUIT_LABELS[card.suit]}`;

export default function KlaverjasPlay() {
  const { code } = useParams<{ code: string }>();
  const location = useLocation() as { state?: { profileId?: string | null } };
  const actions = useKlaverjasSession(code, location.state?.profileId ?? null);

  const data = useKlaverjasLive();
  const { state, status, error, notices } = data;

  if (!state) {
    return (
      <Screen title={`Klaverjassen ${code ?? ''}`} back="/">
        <Panel title="Verbinden">
          <p className="text-sm text-slate-400">
            {status === 'reconnecting'
              ? 'De verbinding is weg. Er wordt opnieuw geprobeerd…'
              : 'Bezig met verbinden met het potje…'}
          </p>
          {error && <p className="mt-2 text-sm text-bad">{error}</p>}
          <p className="mt-3 text-xs text-slate-500">
            Kom je hier terecht zonder gekozen te hebben wie je bent? Ga terug en tik je gezicht aan
            — aan een klaverjastafel heeft iedereen een vaste stoel.
          </p>
        </Panel>
      </Screen>
    );
  }

  return (
    <Screen
      title={state.phase === 'lobby' ? `Code ${state.code}` : `Gift ${state.dealNo}`}
      back="/"
      subtitle={
        state.phase === 'lobby'
          ? `${state.players.filter((p) => p.isClaimed).length} van 4 aan tafel`
          : `${state.options.variant} · ${state.totals[0]} — ${state.totals[1]}`
      }
    >
      {error && (
        <button
          type="button"
          role="alert"
          onClick={() => useKlaverjasLive.getState().dismissError()}
          className="w-full rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-2 text-left text-sm text-red-200"
        >
          {error} <span className="text-red-300/60">— tik om te sluiten</span>
        </button>
      )}

      {state.phase === 'lobby' && <Lobby actions={actions} />}

      {state.phase !== 'lobby' && <KlaverjasTable state={state} mySeat={data.seat} />}

      {state.phase === 'choosing' && <TrumpPanel actions={actions} />}
      {state.phase === 'playing' && <HandPanel actions={actions} />}
      {state.phase === 'scoring' && <DealResult />}
      {state.phase === 'finished' && <Finished />}

      {state.phase !== 'lobby' && <Standings />}

      <Notices items={notices} show={state.phase !== 'lobby'} />
    </Screen>
  );
}

function Lobby({ actions }: { actions: KlaverjasActions }) {
  const data = useKlaverjasLive();
  const state = data.state!;
  const me = myKlaverjasPlayer(data);
  const joinUrl = `${window.location.origin}/klaverjas/live/${state.code}`;
  const allHere = state.players.every((player) => player.isClaimed);
  const allReady = state.players.every((player) => player.isReady);

  return (
    <>
      <Panel title="Deel deze code">
        <p className="text-center font-tabular text-5xl font-bold tracking-[0.2em]">{state.code}</p>
        <p className="mt-3 break-all text-center text-sm text-slate-400">{joinUrl}</p>
        <p className="mt-3 text-center text-sm text-slate-500">
          De tafel staat al klaar. Iedereen tikt zijn eigen gezicht aan en komt op zijn eigen stoel.
        </p>
      </Panel>

      <Panel title="Aan tafel">
        <ul className="space-y-2">
          {state.players.map((player) => (
            <li
              key={player.seat}
              className={`flex items-center gap-3 rounded-xl px-3 py-2 ${
                player.isClaimed ? 'bg-base-700/60' : 'bg-base-800/40'
              }`}
            >
              <span
                className={`h-8 w-1.5 rounded-full ${
                  teamOf(player.seat) === 0 ? 'bg-accent-500' : 'bg-slate-500'
                }`}
                aria-hidden
              />
              <Avatar player={player} size={32} seatColor={seatColor(player.seat)} />
              <span className="flex-1 truncate font-medium">{player.name}</span>
              <span className="text-sm text-slate-500">
                {!player.isClaimed ? 'nog niet' : player.isReady ? 'klaar' : 'wacht'}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-500">
          Maten zitten tegenover elkaar: {state.players[0]?.name} met {state.players[2]?.name},{' '}
          {state.players[1]?.name} met {state.players[3]?.name}.
        </p>
      </Panel>

      <div className="space-y-2">
        {me && !me.isHost && (
          <Button
            full
            variant={me.isReady ? 'secondary' : 'primary'}
            onClick={() => actions.ready(!me.isReady)}
          >
            {me.isReady ? 'Toch nog even niet' : 'Ik ben klaar'}
          </Button>
        )}
        {me?.isHost && (
          <Button full variant="primary" disabled={!allHere || !allReady} onClick={actions.start}>
            {!allHere ? 'Wachten tot iedereen er is' : allReady ? 'Beginnen' : 'Wachten op klaar'}
          </Button>
        )}
      </div>
    </>
  );
}

function TrumpPanel({ actions }: { actions: KlaverjasActions }) {
  const data = useKlaverjasLive();
  const state = data.state!;
  const mine = isMyChoice(data);
  const hand = myKlaverjasHand(data);

  if (!mine) {
    const who = state.chooserSeat !== null ? state.players[state.chooserSeat]?.name : null;
    return (
      <Panel title="Troef">
        <p className="text-sm text-slate-400">
          {who ? `${who} kiest de troef…` : 'Er wordt gekozen…'}
        </p>
        <FannedHand cardWidth={56} className="py-2">
          {hand.map((card) => (
            <PlayingCard key={`${card.suit}-${card.rank}`} card={card} size="md" disabled />
          ))}
        </FannedHand>
      </Panel>
    );
  }

  return (
    <Panel title={state.forcedChoice ? 'Jij moet kiezen' : 'Kies troef, of pas'}>
      <p className="mb-2 text-sm text-slate-400">
        Kies je troef, dan moet jouw team meer dan de helft halen — anders ga je nat en gaat alles
        naar de tegenpartij.
      </p>

      <FannedHand cardWidth={56} className="py-2">
        {hand.map((card) => (
          <PlayingCard key={`${card.suit}-${card.rank}`} card={card} size="md" disabled />
        ))}
      </FannedHand>

      <div className="mt-3 grid grid-cols-4 gap-2">
        {SUITS.map((suit) => (
          <button
            key={suit}
            type="button"
            aria-label={`Troef ${SUIT_LABELS[suit]}`}
            onClick={() => actions.chooseTrump(suit)}
            className={`min-h-14 rounded-xl border border-white/10 bg-base-800/60 text-2xl hover:bg-base-700 ${
              suit === 'hearts' || suit === 'diamonds' ? 'text-red-400' : 'text-slate-200'
            }`}
          >
            {SUIT_SYMBOLS[suit]}
          </button>
        ))}
      </div>

      {state.options.trumpChoice === 'vrij' && !state.forcedChoice && (
        <div className="mt-2">
          <Button full onClick={() => actions.chooseTrump(null)}>
            Passen
          </Button>
        </div>
      )}
      {state.forcedChoice && state.options.trumpChoice === 'vrij' && (
        <p className="mt-2 text-sm text-slate-500">
          Iedereen is langs geweest — passen kan niet meer.
        </p>
      )}
    </Panel>
  );
}

function HandPanel({ actions }: { actions: KlaverjasActions }) {
  const data = useKlaverjasLive();
  const state = data.state!;
  const seat = data.seat!;
  const trump = state.trump!;
  const hand = sortKlaverjasHand(myKlaverjasHand(data), trump);
  const playable = isMyKlaverjasTurn(data);
  const [refused, setRefused] = useState<string | null>(null);

  const trickLength = state.currentTrick.length;
  useEffect(() => setRefused(null), [trickLength, state.dealNo]);

  /*
   * The same rule function the server uses, so what is greyed out here and what
   * would be refused there cannot drift apart. The server still decides.
   */
  const context = { trick: state.currentTrick, trump, variant: state.options.variant, seat };
  const legal = playable ? klaverjasLegalPlays(hand, context) : [];
  const isLegal = (card: Card) => legal.some((ok) => cardsEqual(ok, card));

  const waitingOn =
    !playable && state.turnSeat !== null ? state.players[state.turnSeat]?.name : null;

  /*
   * The knock, when the table plays that way.
   *
   * Always there while a deal is running, and deliberately not only when there is
   * something to collect: a button that appears exactly when you have twenty
   * points waiting would do the noticing for you, which is the one thing this
   * option exists to stop. So it sits there, and the server decides.
   */
  const mustKnock = state.options.roemClaim === 'zelf';

  return (
    <Panel
      title={playable ? 'Jij bent aan zet' : 'Jouw kaarten'}
      action={
        mustKnock ? (
          <button
            type="button"
            onClick={actions.claimRoem}
            className="rounded-full bg-base-700 px-3 py-1 text-sm font-medium text-accent-200 hover:bg-base-600"
          >
            Roem!
          </button>
        ) : undefined
      }
    >
      {!playable && waitingOn && (
        <p className="mb-3 text-sm text-slate-400">Wachten op {waitingOn}…</p>
      )}

      <FannedHand cardWidth={56} className="py-2">
        {hand.map((card, index) => {
          const allowed = isLegal(card);
          return (
            <PlayingCard
              key={`${card.suit}-${card.rank}`}
              card={card}
              size="md"
              dimmed={playable && !allowed}
              disabled={!playable}
              onClick={() => {
                if (allowed) {
                  setRefused(null);
                  actions.play(card);
                } else {
                  setRefused(whyIllegal(card, hand, context));
                  playCue({ kind: 'refused' });
                }
              }}
              label={`Speel ${cardName(card)}${playable && !allowed ? ' (mag nu niet)' : ''}`}
              className="animate-deal-in"
              style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
            />
          );
        })}
      </FannedHand>

      {refused && (
        <p
          role="alert"
          className="mt-3 animate-pop-in rounded-xl border-l-2 border-amber-400 bg-amber-400/10 px-3 py-2 text-sm text-amber-100"
        >
          {refused}
        </p>
      )}
    </Panel>
  );
}

function DealResult() {
  const state = useKlaverjasLive((data) => data.state)!;
  const deal = state.deals[state.deals.length - 1];
  if (!deal) return null;

  const names = ([0, 1] as const).map((team) =>
    state.players
      .filter((player) => teamOf(player.seat) === team)
      .map((player) => player.name)
      .join(' & ')
  );

  return (
    <Panel title={`Gift ${deal.dealNo} geteld`}>
      {deal.nat && (
        <p className="mb-3 animate-pop-in rounded-xl border border-bad/40 bg-bad/10 px-3 py-2 text-center font-medium text-red-100">
          {names[deal.playingTeam]} gaat nat — alles naar {names[1 - deal.playingTeam]}.
        </p>
      )}
      {deal.pitTeam !== null && (
        <p className="mb-3 animate-pop-in rounded-xl border border-accent-500/40 bg-accent-500/10 px-3 py-2 text-center font-medium text-accent-200">
          Pit! {names[deal.pitTeam]} pakte alle acht de slagen.
        </p>
      )}

      <ul className="space-y-1 text-sm">
        {([0, 1] as const).map((team) => (
          <li key={team} className="flex items-center gap-2">
            <span className="flex-1 truncate">{names[team]}</span>
            <span className="text-slate-400">
              {deal.taken[team]}
              {deal.roem[team] > 0 && ` + ${deal.roem[team]} roem`}
            </span>
            <span className="w-12 text-right font-semibold tabular-nums">{deal.points[team]}</span>
            <span className="w-12 text-right tabular-nums text-slate-500">{deal.totals[team]}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-slate-500">De volgende gift begint zo vanzelf.</p>
    </Panel>
  );
}

function Standings() {
  const state = useKlaverjasLive((data) => data.state)!;
  const names = ([0, 1] as const).map((team) =>
    state.players
      .filter((player) => teamOf(player.seat) === team)
      .map((player) => player.name)
      .join(' & ')
  );

  return (
    <Panel title="Stand — hoogste wint">
      <ul className="space-y-2">
        {([0, 1] as const).map((team) => (
          <li key={team} className="flex items-center gap-3">
            <span
              className={`h-8 w-1.5 rounded-full ${team === 0 ? 'bg-accent-500' : 'bg-slate-500'}`}
              aria-hidden
            />
            <span className="flex-1 truncate">{names[team]}</span>
            <span className="text-2xl font-bold tabular-nums">{state.totals[team]}</span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Finished() {
  const state = useKlaverjasLive((data) => data.state)!;
  const names = ([0, 1] as const).map((team) =>
    state.players
      .filter((player) => teamOf(player.seat) === team)
      .map((player) => player.name)
      .join(' & ')
  );
  const winner =
    state.totals[0] === state.totals[1] ? null : state.totals[0] > state.totals[1] ? 0 : 1;

  return (
    <Panel title="Afgelopen">
      <p className="text-center text-lg font-semibold">
        {winner === null
          ? 'Gelijkspel.'
          : `${names[winner]} wint met ${state.totals[winner]} tegen ${state.totals[1 - winner]}.`}
      </p>
    </Panel>
  );
}
